import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Section from '../components/Section';
import Button from '../components/Button';
import PracticeAreaCTA from '../components/PracticeAreaCTA';
import SEO from '../components/SEO';

// Constants for practice area IDs
const PRACTICE_AREA_IDS = {
  ARV_OG_SKIFTE: 'arv-og-skifte',
  ARBEIDSRETT: 'arbeidsrett',
  FAST_EIENDOM: 'fast-eiendom',
} as const;

interface PracticeAreaData {
  id: string;
  title: string;
  icon: string;
  lawyers: string[];
  content: string[];
  landingPageUrl?: string;
}

const practiceAreasData: PracticeAreaData[] = [
  {
    id: PRACTICE_AREA_IDS.ARV_OG_SKIFTE,
    title: 'Arv og Skifte',
    icon: '⚖️',
    lawyers: ['Thorleif G. Bruland', 'Tove Kristin Farstad'],
    landingPageUrl: '/arv-og-testament',
    content: [
      'Vi bistår ved opprettelse av testament. Vi kan også gi råd dersom du ønsker å gi større gaver i live. Dersom du sitter i uskifte er det begrensinger i gaveadgangen. Vi kan gi råd og bistand knyttet til dette.',
      'Vi kan også bistå ved arveoppgjør. Valg av løsninger kan ha stor økonomisk betydning og det er viktig å søke bistand dersom man er usikker.',
    ],
  },
  {
    id: PRACTICE_AREA_IDS.ARBEIDSRETT,
    title: 'Arbeidsrett',
    icon: '💼',
    lawyers: ['Thorleif G. Bruland'],
    landingPageUrl: '/arbeidsrett',
    content: [
      'Vi bistår både arbeidsgivere og arbeidstakere. Vi gir råd om hvordan man skal gå frem i enkeltsaker. Vi deltar også i forhandlinger mellom arbeidsgiver og arbeidstaker samt ved domstolsbehandling.',
      'Konflikter knyttet til arbeidsforhold er stressende for dem det gjelder. De legger beslag på den ansattes og bedriftens ressurser. Vi er opptatt av å finne gode løsninger og å redusere konfliktnivået i den enkelte sak.',
      'Vi bistår blant annet ved ansettelse, varsling, oppsigelse, og ved utforming av konkurranseklausuler.',
      'Oppstår det problemer gir vi konkrete råd om hvordan man kan gå frem for å unngå en større konflikt.',
    ],
  },
  {
    id: PRACTICE_AREA_IDS.FAST_EIENDOM,
    title: 'Fast Eiendom',
    icon: '🏠',
    lawyers: ['Thorleif G. Bruland', 'Tove Kristin Farstad'],
    landingPageUrl: '/fast-eiendom',
    content: [
      'Firmaet gir råd i forbindelse med tolking av leiekontrakter, ved kjøp og salg av eiendom, ved oppføring av nye bygg og vedlikehold av eldre bygninger. Vi representerer også klienter i forhold til offentlige myndigheter og naboer.',
      'Advokatene gir råd til kjøpere og selgere i forbindelse med kjøp- og salg av fast eiendom. Vi bistår med utredning av veirettigheter, ferdselsrettigheter etter friluftsloven og tinglyste bestemmelser knyttet til utbygging.',
      'Vi har kompetanse på eiendomsforvaltning og drift av fast eiendom. I eiendomssaker utredes problemstillingene i nært samarbeid med eksperter, f eks takseringskyndige, arktitekt eller statiker.',
      'Mange saker løses på et tidlig stadium ved at man gjør en god utredning av grunnlaget for tvisten og forhandler med motpart på bakgrunn av dette. I den utstrekning en sak havner i retten er vi opptatt av at det faktiske grunnlaget i saken er klart.',
    ],
  },
];

const PracticeAreas: React.FC = () => {
  const location = useLocation();

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Advokatfirmaet Bruland - Fagområder",
    "url": "https://bruland.no/fagomrader",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Fagområder",
      "itemListElement": practiceAreasData.map(area => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": area.title,
          "description": area.content[0]
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Fagområder - Juridisk Bistand i Oslo | Arbeidsrett, Eiendom"
        description="Vi har spesialkompetanse innen arv og skifte, arbeidsrett og fast eiendom. Erfarne advokater som bistår deg med juridisk bistand i Oslo."
        keywords="fagområder advokat oslo, juridisk bistand, arbeidsrett, eiendomsrett, arbeidstvister, arv og skifte"
        canonicalUrl="/fagomrader"
        structuredData={structuredData}
      />
      {/* Page Header */}
      <Section
        title="Fagområder"
        subtitle="Advokater med spesialkompetanse"
        background="gray"
        spacing="md"
        centered
      />

      {/* Introduction */}
      <Section background="white" spacing="lg" container="narrow">
        <p className="text-lg leading-relaxed text-gray-700 text-center">
          Vi arbeider målrettet for å løse klientenes problemer. Lang erfaring med ulike typer av saker gjør at vi ser helhetsbildet og kan håndtere sammensatte problemstillinger. Vi finner løsninger i nært samarbeid med klientene. Vi gir råd i enkeltsaker og bistår i forbindelse med tvisteløsning.
        </p>
      </Section>

      {/* Quick Navigation */}
      <Section background="gray" spacing="sm">
        <div className="flex flex-wrap justify-center gap-4">
          {practiceAreasData.map((area) => (
            <a
              key={area.id}
              href={`#${area.id}`}
              className="px-6 py-3 bg-white border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-all duration-200 flex items-center gap-2"
            >
              <span className="text-xl">{area.icon}</span>
              {area.title}
            </a>
          ))}
        </div>
      </Section>

      {/* Practice Areas */}
      {practiceAreasData.map((area, index) => (
        <Section
          key={area.id}
          background={index % 2 === 0 ? 'white' : 'gray'}
          spacing="lg"
        >
          <div id={area.id} className="scroll-mt-20">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content - 2/3 width */}
                <div className="lg:col-span-2">
                  {/* Title with Icon */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl">{area.icon}</span>
                    </div>
                    <h2 className="text-4xl font-bold text-gray-900 font-serif">
                      {area.title}
                    </h2>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    {area.content.map((paragraph, idx) => (
                      <p key={idx} className="text-lg leading-relaxed text-gray-700">
                        {paragraph}
                      </p>
                    ))}

                    {/* CTA for practice areas with dedicated landing pages */}
                    {area.landingPageUrl && (
                      <PracticeAreaCTA
                        title={area.title}
                        landingPageUrl={area.landingPageUrl}
                      />
                    )}
                  </div>
                </div>

                {/* Sidebar - 1/3 width */}
                <div className="lg:col-span-1">
                  <div className="bg-primary/5 border-l-4 border-accent p-6 rounded-lg sticky top-24">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 font-serif">
                      Ansvarlige advokater
                    </h3>
                    <div className="space-y-3">
                      {area.lawyers.map((lawyer, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 text-gray-700"
                        >
                          <svg className="w-5 h-5 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">{lawyer}</span>
                        </div>
                      ))}
                    </div>

                    {/* Contact CTA */}
                    <div className="mt-6 pt-6 border-t border-gray-300">
                      <p className="text-sm text-gray-600 mb-3">
                        Trenger du bistand innen {area.title.toLowerCase()}?
                      </p>
                      <Link to="/kontakt">
                        <Button size="sm" fullWidth>
                          Kontakt oss
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      ))}

      {/* Bottom CTA */}
      <section className="bg-primary text-white text-center py-20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-8 font-serif text-white">
            Trenger du juridiske råd?
          </h2>
          <Link to="/kontakt">
            <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary">
              Forespør en samtale →
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default PracticeAreas;
